import React, {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import {
  AutoModalPriority,
  useAutoModal,
} from '../../../../common/AutoModalProvider';
import ErrorBoundaryWithCrashReporting from '../../../../components/Error/ErrorBoundaryWithCrashReporting';
import { OpenRequestModalCallbackProps } from './constants';
import { useOpenAssistanceRequestModalByQuery } from './hooks/useOpenAssistanceRequestModalByQuery';
import { SubscriptionAssistanceRequestType } from './types';

type SubscriptionAssistanceRequestModalContextType = {
  openAssistanceRequestModal: (options: OpenRequestModalCallbackProps) => void;
  isLoadingModal: boolean;
};

const SubscriptionAssistanceRequestModal = lazy(() =>
  import('./SubscriptionAssistanceRequestModal').then(mod => ({
    default: mod.SubscriptionAssistanceRequestModal,
  }))
);

export const SubscriptionAssistanceRequestModalContext = createContext<
  SubscriptionAssistanceRequestModalContextType | undefined
>(undefined);

type Props = {
  children: React.ReactNode;
  enableAutoTriggers?: boolean;
};

export const SubscriptionAssistanceRequestModalProvider = ({
  children,
  enableAutoTriggers = true,
}: Props) => {
  const [isModalOpen, setIsOpenModal] = useState(false);
  const [requestTalentSearchChats, setRequestTalentSearchChats] = useQueryParam(
    'requestTalentSearchChats',
    BooleanParam
  );

  const [modalData, setModalData] =
    useState<OpenRequestModalCallbackProps | null>(null);
  const [autoRequest, setAutoRequest] =
    useState<OpenRequestModalCallbackProps | null>(null);
  const canShowAutoRequest = useAutoModal(
    AutoModalPriority.Transaction,
    Boolean(autoRequest)
  );
  const [isLoadingModal, startTransition] = useTransition();

  const openAssistanceRequestModal = useCallback(
    (options: OpenRequestModalCallbackProps) => {
      startTransition(() => {
        setIsOpenModal(true);
        setModalData({
          selectedPlanType: options.selectedPlanType,
          subscriptionAssistanceRequestType:
            options.subscriptionAssistanceRequestType,
          prefillMessage: options.prefillMessage,
        });
      });
    },
    []
  );

  useOpenAssistanceRequestModalByQuery({
    openAssistanceRequestModal: setAutoRequest,
    enabled: enableAutoTriggers,
  });

  const handleCloseModal = () => {
    if (isModalOpen) {
      setIsOpenModal(false);
      setModalData(null);
    } else {
      setAutoRequest(null);
    }
  };

  useEffect(() => {
    if (enableAutoTriggers && requestTalentSearchChats) {
      setAutoRequest({
        subscriptionAssistanceRequestType:
          SubscriptionAssistanceRequestType.TALENT_SEARCH_CHAT_ADDITION,
      });
      setRequestTalentSearchChats(undefined);
    }
  }, [
    enableAutoTriggers,
    requestTalentSearchChats,
    setRequestTalentSearchChats,
  ]);

  return (
    <SubscriptionAssistanceRequestModalContext.Provider
      value={{
        openAssistanceRequestModal: openAssistanceRequestModal,
        isLoadingModal,
      }}
    >
      {(isModalOpen || (autoRequest && canShowAutoRequest)) && (
        <ErrorBoundaryWithCrashReporting fallback={null}>
          <Suspense fallback={null}>
            <SubscriptionAssistanceRequestModal
              selectedPlanType={
                (isModalOpen ? modalData : autoRequest)?.selectedPlanType
              }
              subscriptionAssistanceRequestType={
                (isModalOpen ? modalData : autoRequest)
                  ?.subscriptionAssistanceRequestType
              }
              onClose={handleCloseModal}
              prefillMessage={
                (isModalOpen ? modalData : autoRequest)?.prefillMessage
              }
            />
          </Suspense>
        </ErrorBoundaryWithCrashReporting>
      )}
      {children}
    </SubscriptionAssistanceRequestModalContext.Provider>
  );
};
